import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, ArrowDownCircle, ArrowUpCircle, Package, Plus, Search } from "lucide-react";

import api from "../services/api";
import { fetchStockCatalogs } from "../services/stockCatalogs";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import EditStockItemModal from "../components/stock/EditStockItemModal";
import { usePermissions } from "../context/PermissionsContext";
import usePagedList from "../hooks/usePagedList";
import PaginationBar from "../components/ui/PaginationBar";
import ExportXlsxButton from "../components/ui/ExportXlsxButton";
import { parseApiDateTime } from "../utils/datetime";
import StockMovementModal from "../components/stock/StockMovementModal";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value || 0));

const formatNumber = (value) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  const date = parseApiDateTime(value);
  if (!date) return "—";
  return date.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });
};

const statusStyles = {
  "Crítico": "bg-red-100 text-red-700",
  Critico: "bg-red-100 text-red-700",
  "Ponto de Pedido": "bg-amber-100 text-amber-700",
  "Saudável": "bg-green-100 text-green-700",
  Saudavel: "bg-green-100 text-green-700",
  Inativo: "bg-gray-100 text-gray-700",
};

const movementStyles = {
  entrada: "bg-green-100 text-green-700",
  saida: "bg-red-100 text-red-700",
  ajuste: "bg-blue-100 text-blue-700",
};

const summaryCards = (summary) => [
  {
    title: "Itens ativos",
    value: formatNumber(summary?.active_items),
    subtitle: `${formatNumber(summary?.total_items)} cadastrados`,
    icon: Package,
  },
  {
    title: "Baixo estoque",
    value: formatNumber(summary?.low_stock_count),
    subtitle: "Abaixo do mínimo",
    icon: AlertTriangle,
  },
  {
    title: "Ponto de pedido",
    value: formatNumber(summary?.reorder_count),
    subtitle: "Itens para reabastecer",
    icon: Activity,
  },
  {
    title: "Valor estimado",
    value: formatCurrency(summary?.total_estimated_value),
    subtitle: `${formatNumber(summary?.consumption_30d)} consumidos em 30d`,
    icon: ArrowUpCircle,
  },
];

const Stock = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const pg = usePagedList(items);
  const [movements, setMovements] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [alertFilter, setAlertFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [catalogs, setCatalogs] = useState({
    categories: [],
    subcategories: [],
    suppliers: [],
    locations: [],
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [movementType, setMovementType] = useState("entrada");
  const [isMovementOpen, setIsMovementOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const fetchStockData = async () => {
    try {
      setLoading(true);

      const params = {};
      if (search) params.search = search;
      if (alertFilter) params.alert = alertFilter;
      if (categoryFilter) params.category_id = categoryFilter;
      if (subcategoryFilter) params.subcategory_id = subcategoryFilter;
      if (supplierFilter) params.supplier_id = supplierFilter;
      if (locationFilter) params.location_id = locationFilter;

      const [
        summaryResponse,
        itemsResponse,
        movementsResponse,
        catalogsResponse,
        collaboratorsResponse,
        ticketsResponse,
      ] = await Promise.all([
        api.get("/stock/summary"),
        api.get("/stock/items", { params }),
        api.get("/stock/movements", { params: { limit: 12 } }),
        fetchStockCatalogs(true),
        api.get("/collaborators/listbox"),
        api.get("/tickets/", { params: { limit: 200 } }),
      ]);

      setSummary(summaryResponse.data || null);
      setItems(Array.isArray(itemsResponse.data) ? itemsResponse.data : []);
      setMovements(Array.isArray(movementsResponse.data) ? movementsResponse.data : []);
      setCatalogs(catalogsResponse);
      setCollaborators(Array.isArray(collaboratorsResponse.data) ? collaboratorsResponse.data : []);
      setTickets(Array.isArray(ticketsResponse.data) ? ticketsResponse.data : []);
    } catch (error) {
      console.error("Erro ao carregar dados de estoque:", error);
      setSummary(null);
      setItems([]);
      setMovements([]);
      setCollaborators([]);
      setTickets([]);
      setCatalogs({ categories: [], subcategories: [], suppliers: [], locations: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStockData();
  }, [search, alertFilter, categoryFilter, subcategoryFilter, supplierFilter, locationFilter]);

  const openMovement = (item, type) => {
    setSelectedItem(item);
    setMovementType(type);
    setIsMovementOpen(true);
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setIsEditOpen(true);
  };

  const closeModals = () => {
    setSelectedItem(null);
    setIsMovementOpen(false);
    setIsEditOpen(false);
  };

  const handleMovementSubmit = async (payload) => {
    try {
      await api.post("/stock/movements", payload);
      closeModals();
      await fetchStockData();
    } catch (error) {
      console.error("Erro ao salvar movimentação:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao salvar movimentação.");
    }
  };

  const handleEditSubmit = async (payload) => {
    if (!selectedItem?.id) return;

    try {
      await api.put(`/stock/items/${selectedItem.id}`, payload);
      closeModals();
      await fetchStockData();
    } catch (error) {
      console.error("Erro ao atualizar item de estoque:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao atualizar item de estoque.");
    }
  };

  const cards = useMemo(() => summaryCards(summary), [summary]);
  const availableSubcategories = catalogs.subcategories.filter((subcategory) => {
    if (!categoryFilter) return true;
    return String(subcategory.category_id) === String(categoryFilter);
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Estoque</h1>
            <p className="text-gray-500">
              Controle de materiais, abastecimento, consumo e alertas operacionais.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <ExportXlsxButton url="/stock/export" filename="estoque.xlsx" />
            {can("create", "/stock") && (
              <Button onClick={() => navigate("/stock/new")}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Item
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/stock/purchase-requests")}>
              Compras
            </Button>
            <Button variant="outline" onClick={() => navigate("/stock/requests")}>
              Requisições
            </Button>
            <Button variant="outline" onClick={() => navigate("/stock/catalogs")}>
              Cadastros
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.title}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm text-gray-500">{card.title}</p>
                      <p className="mt-2 text-2xl font-semibold text-gray-900">{card.value}</p>
                      <p className="mt-1 text-xs text-gray-500">{card.subtitle}</p>
                    </div>
                    <div className="rounded-full bg-blue-50 p-3 text-blue-600">
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Buscar por nome, SKU, categoria, subcategoria, fornecedor ou localização..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <select
                  value={alertFilter}
                  onChange={(e) => setAlertFilter(e.target.value)}
                  className="h-11 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm"
                >
                  <option value="">Todos os itens</option>
                  <option value="low_stock">Baixo estoque</option>
                  <option value="reorder">Ponto de pedido</option>
                  <option value="healthy">Saudáveis</option>
                  <option value="inactive">Inativos</option>
                </select>
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setSubcategoryFilter("");
                  }}
                  className="h-11 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm"
                >
                  <option value="">Todas as categorias</option>
                  {catalogs.categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="h-11 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm"
                >
                  <option value="">Todos os fornecedores</option>
                  {catalogs.suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
                <select
                  value={subcategoryFilter}
                  onChange={(e) => setSubcategoryFilter(e.target.value)}
                  className="h-11 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm"
                >
                  <option value="">Todas as subcategorias</option>
                  {availableSubcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="h-11 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm"
                >
                  <option value="">Todas as localizações</option>
                  {catalogs.locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="py-10 text-center text-gray-500">Carregando estoque...</div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-gray-500">
                Nenhum item encontrado para os filtros atuais.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Item</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Categoria</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Saldo</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Mín / Pedido</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Consumo / Giro</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Valor</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Localização</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pg.pageItems.map((item) => (
                      <tr key={item.id} className="border-b border-gray-100 align-top hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">{item.name}</div>
                          <div className="text-xs text-gray-500">{item.sku || "Sem SKU"}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div>{item.category_name || "N/A"}</div>
                          <div className="text-xs text-gray-500">{item.subcategory_name || "Sem subcategoria"}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">
                            {formatNumber(item.quantity)} {item.unit || "Un"}
                          </div>
                          <div className="text-xs text-gray-500">
                            Cobertura: {item.coverage_days ? `${formatNumber(item.coverage_days)} dias` : "sem consumo"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div>Mín: {formatNumber(item.minimum_stock)}</div>
                          <div>Pedido: {formatNumber(item.reorder_point)}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div>30d: {formatNumber(item.consumption_30d)}</div>
                          <div>Giro: {formatNumber(item.turnover_30d)}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div>{formatCurrency(item.estimated_value)}</div>
                          <div className="text-xs text-gray-500">
                            CM: {formatCurrency(item.average_cost)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              statusStyles[item.stock_status] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {item.stock_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div>{item.location_name || "N/A"}</div>
                          <div className="text-xs text-gray-500">{item.supplier_name || "Sem fornecedor"}</div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              onClick={() => openMovement(item, "entrada")}
                              className="h-auto px-3 py-1 text-xs border-green-200 text-green-700 hover:bg-green-50"
                            >
                              <ArrowUpCircle className="mr-1 h-3.5 w-3.5" />
                              Entrada
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => openMovement(item, "saida")}
                              className="h-auto px-3 py-1 text-xs border-red-200 text-red-700 hover:bg-red-50"
                            >
                              <ArrowDownCircle className="mr-1 h-3.5 w-3.5" />
                              Saída
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => openEdit(item)}
                              className="h-auto px-3 py-1 text-xs text-gray-700"
                            >
                              Editar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PaginationBar
                  page={pg.page}
                  pageSize={pg.pageSize}
                  total={pg.total}
                  onPageChange={pg.onPageChange}
                  onPageSizeChange={pg.onPageSizeChange}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Movimentações Recentes</h2>
              <p className="text-sm text-gray-500">Últimas entradas, saídas e ajustes do estoque.</p>
            </div>
          </CardHeader>
          <CardContent>
            {movements.length === 0 ? (
              <div className="py-8 text-center text-gray-500">Nenhuma movimentação registrada.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Item</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Tipo</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Quantidade</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Motivo</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Referência</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Vínculos</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">NF</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Saldo após</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Usuário</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Quando</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((movement) => (
                      <tr key={movement.id} className="border-b border-gray-100">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">{movement.item_name || "Item removido"}</div>
                          <div className="text-xs text-gray-500">{movement.item_sku || "Sem SKU"}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              movementStyles[movement.type] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {movement.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{formatNumber(movement.quantity)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{movement.movement_reason || "—"}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{movement.reference || "—"}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div>{movement.collaborator_name || "Sem colaborador"}</div>
                          <div className="text-xs text-gray-500">
                            {movement.ticket_title ? `#${movement.ticket_id} - ${movement.ticket_title}` : "Sem chamado"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{movement.invoice_number || "—"}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{formatNumber(movement.balance_after)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{movement.user}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(movement.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <StockMovementModal
          isOpen={isMovementOpen}
          onClose={closeModals}
          item={selectedItem}
          collaborators={collaborators}
          tickets={tickets}
          defaultType={movementType}
          onSubmit={handleMovementSubmit}
        />

        <EditStockItemModal
          isOpen={isEditOpen}
          onClose={closeModals}
          item={selectedItem}
          onSubmit={handleEditSubmit}
        />
      </div>
    </Layout>
  );
};

export default Stock;
